import { useState } from 'react';
import { CheckCircle2, ShieldCheck, Clock, Phone, UploadCloud, AlertCircle } from 'lucide-react';

const serviceOptions = [
  'Skruefundering generelt',
  'Jordskruer',
  'GroundPlug',
  'Terrasseskruer',
  'Jordanker',
  'Modulbyggeri / Sommerhus',
  'Erhverv & Industri',
  'Andet / Rådgivning'
];

export default function ContactForm({
  title = 'Få et uforpligtende tilbud',
  subtitle = 'Udfyld formularen, så vender vi hurtigt tilbage med vejledning og et skarpt tilbud.',
  defaultService = '',
  variant = 'default', // 'default' | 'hero' | 'card'
  compact = false,
  className = ''
}) {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [fileError, setFileError] = useState('');
  const [selectedService, setSelectedService] = useState(defaultService);
  const [fileCount, setFileCount] = useState(0);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFileError('');
    setFileCount(files.length);

    if (files.length > 5) {
      setFileError('Du kan højst vedhæfte 5 filer.');
      e.target.value = '';
      setFileCount(0);
      return;
    }

    let totalSize = 0;
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp'];

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setFileError(`Filen "${file.name}" er for stor. Hver fil må højst være 10 MB.`);
        e.target.value = '';
        setFileCount(0);
        return;
      }
      totalSize += file.size;

      const fileType = file.type;
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const isValidType = allowedTypes.includes(fileType) || allowedExtensions.includes(fileExtension);
      if (!isValidType) {
        setFileError(`Filen "${file.name}" har et ugyldigt format. Kun PDF, PNG, JPEG, GIF og WEBP er tilladt.`);
        e.target.value = '';
        setFileCount(0);
        return;
      }
    }

    if (totalSize > 20 * 1024 * 1024) {
      setFileError('Den samlede størrelse af filerne må ikke overstige 20 MB.');
      e.target.value = '';
      setFileCount(0);
      return;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fileError) {
      return;
    }
    setStatus('loading');

    const formData = new FormData(e.target);

    // Sæt reply-to mail til den indtastede email
    const email = formData.get('email');
    if (email) {
      formData.append('_replyto', email);
      formData.append('reply_to', email);
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
        setFileError('');
        setFileCount(0);
      } else {
        const errData = await response.json().catch(() => ({}));
        console.error('Submission failed:', errData);
        setStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
    }
  };

  const isHero = variant === 'hero';

  return (
    <div 
      className={`bg-white text-slate-900 shadow-2xl rounded-2xl border border-slate-200/80 overflow-hidden ${
        isHero ? 'p-5 sm:p-7 md:p-8 backdrop-blur-xl bg-white/98' : compact ? 'p-5 sm:p-6' : 'p-6 sm:p-8 md:p-10'
      } ${className}`}
    >
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-xs font-bold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
            Hurtig Svartid
          </span>
          <span className="text-xs text-slate-500 font-medium">Uforpligtende</span>
        </div>
        <h2 className={`font-black uppercase tracking-tight text-slate-900 ${isHero || compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'}`}>
          {title}
        </h2>
        {subtitle && (
          <p className="text-slate-600 text-xs sm:text-sm mt-1.5 leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>

      {status === 'success' ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 p-6 rounded-xl text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <p className="font-bold text-lg">Tak for din henvendelse!</p>
          <p className="text-sm text-emerald-800">
            Vi har modtaget dine oplysninger og vender tilbage med en vurdering eller et tilbud hurtigst muligt (typisk inden for 24 timer).
          </p>
          <button 
            onClick={() => setStatus('idle')}
            className="mt-2 inline-flex items-center justify-center px-4 py-2 text-xs font-bold uppercase tracking-wider bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors"
          >
            Send en ny henvendelse
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot for spam protection */}
          <div className="sr-only absolute opacity-0 -z-10">
            <label htmlFor="website_url">Website URL</label>
            <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
          </div>

          {/* Service vælger */}
          <div>
            <label htmlFor="service" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1">
              Hvad omhandler projektet?
            </label>
            <select
              id="service"
              name="service"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 font-medium focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all cursor-pointer"
            >
              <option value="">Vælg emne / løsning (valgfrit)</option>
              {serviceOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* Navn & Telefon */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div>
              <label htmlFor="name" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1">
                Navn *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all"
                placeholder="Dit fulde navn"
              />
            </div>
            <div>
              <label htmlFor="phone" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1">
                Telefon *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all"
                placeholder="Dit telefonnummer"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all"
              placeholder="din@email.dk"
            />
          </div>

          {/* Besked */}
          <div>
            <label htmlFor="message" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1">
              Beskrivelse af opgave / projekt *
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={isHero || compact ? 3 : 4}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none transition-all resize-none"
              placeholder="F.eks. type konstruktion, placering, anslået antal skruer eller jordforhold..."
            ></textarea>
          </div>

          {/* Filvedhæftning */}
          <div className="pt-1">
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="files" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                <UploadCloud className="w-3.5 h-3.5 text-slate-500" />
                <span>Vedhæft tegninger / rapporter</span>
              </label>
              {fileCount > 0 && (
                <span className="text-[11px] font-semibold text-emerald-600">
                  {fileCount} {fileCount === 1 ? 'fil valgt' : 'filer valgt'}
                </span>
              )}
            </div>
            <input
              type="file"
              id="files"
              name="files"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
              onChange={handleFileChange}
              className="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Maks. 5 filer (PDF, PNG, JPG, WEBP). Ingeniørberegninger, geoteknisk rapport m.m.
            </p>
            {fileError && (
              <p className="text-red-600 text-xs font-medium mt-1 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{fileError}</span>
              </p>
            )}
          </div>

          {status === 'error' && (
            <p className="text-red-600 text-xs font-medium bg-red-50 p-2.5 rounded-lg border border-red-200">
              Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte til os på +45 26 79 61 39.
            </p>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full bg-slate-950 hover:bg-slate-800 text-white py-3.5 px-6 rounded-xl text-sm font-bold uppercase tracking-wider transition-all shadow-lg hover:shadow-slate-950/20 active:scale-[0.99] disabled:bg-slate-400 cursor-pointer flex items-center justify-center gap-2"
          >
            {status === 'loading' ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                <span>Sender henvendelse...</span>
              </span>
            ) : (
              <span>Send henvendelse nu</span>
            )}
          </button>

          {/* Trust indicators */}
          <div className="pt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500 border-t border-slate-100">
            <span className="flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              100% uforpligtende
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-amber-600" />
              Hurtigt svar
            </span>
            <a href="tel:+4526796139" className="flex items-center gap-1 text-slate-700 hover:text-slate-900 font-semibold transition-colors">
              <Phone className="w-3 h-3 text-slate-700" />
              Ring 26 79 61 39
            </a>
          </div>
        </form>
      )}
    </div>
  );
}
